import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import PostList from '../../components/PostList';
import { postAPI } from '../../api';

const SearchContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0.5rem 0;
`;

const SearchContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const SearchBox = styled.div`
  width: 100%;
  padding: 20px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    padding: 15px 10px;
  }
`;

const SearchHeader = styled.div`
  margin-bottom: 1.5rem;
`;

const SearchTitle = styled.h1`
  font-size: 24px;
  color: #333;
  margin-bottom: 10px;
  
  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const SearchForm = styled.form`
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const SearchSelect = styled.select`
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 15px;
  height: 48px;
  background-color: white;
  color: #444;
  min-width: 120px;
  
  &:focus {
    border-color: #4a90e2;
    outline: none;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    height: 48px;
  }
`;

const SearchInput = styled.input`
  padding: 10px 15px;
  border: 1px solid #ddd;
  font-size: 15px;
  margin: 0 8px;
  height: 48px;
  border-radius: 4px;
  flex: 1;
  
  &:focus {
    border-color: #4a90e2;
    outline: none;
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.1);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    margin: 8px 0;
  }
`;

const SearchButton = styled.button`
  background-color: #4a90e2;
  color: white;
  border: none;
  padding: 0 20px;
  font-size: 15px;
  cursor: pointer;
  height: 48px;
  border-radius: 4px;
  font-weight: 500;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #3a7bc8;
  }
  
  &:active {
    background-color: #2c6cb0;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    height: 48px;
  }
`;

const ResultsContainer = styled.div`
  width: 100%;
  padding: 20px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  
  @media (max-width: 768px) {
    padding: 15px 10px;
  }
`;

const ResultInfo = styled.div`
  font-size: 16px;
  color: #666;
  margin-bottom: 1rem;
  
  strong {
    color: #4a90e2;
    font-weight: 600;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 50px 0;
  font-size: 16px;
  color: #666;
`;

const ErrorMessage = styled.div`
  color: #e53e3e;
  font-size: 14px;
  margin-top: 5px;
`;

const Search: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // 쿼리 파라미터에서 검색어와 검색 타입 가져오기
  const query = new URLSearchParams(location.search);
  const initialSearchQuery = query.get('q') || '';
  const initialSearchType = query.get('type') || '1';
  
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  const [searchType, setSearchType] = useState(initialSearchType);
  const [loading, setLoading] = useState(false);
  const [posts, setPosts] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20
  });

  // 검색 실행 함수
  const executeSearch = useCallback(async (query: string, type: string, page: number = 1) => {
    if (!query) return;
    
    if (query.length < 3) {
      setError('검색어는 최소 3자 이상 입력해주세요.');
      return;
    }
    
    setError(null);
    setLoading(true);
    try {
      const response = await postAPI.getPostList(page, 20, query, Number(type));
      setPosts(response.data.posts || []);
      setPagination({
        currentPage: response.data.pagination?.currentPage || 1,
        totalPages: response.data.pagination?.totalPages || 1,
        totalItems: response.data.pagination?.totalItems || 0,
        itemsPerPage: response.data.pagination?.itemsPerPage || 20
      });
    } catch (error) {
      console.error('검색 오류:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // 검색어 또는 타입이 변경되면 URL 업데이트 및 검색 실행
  useEffect(() => {
    if (initialSearchQuery) {
      if (initialSearchQuery.length < 3) {
        setError('검색어는 최소 3자 이상 입력해주세요.');
        return;
      }
      executeSearch(initialSearchQuery, initialSearchType);
    }
  }, [initialSearchQuery, initialSearchType, executeSearch]);

  // 검색 폼 제출 처리
  const handleSearch = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    
    if (searchQuery.trim().length < 3) {
      setError('검색어는 최소 3자 이상 입력해주세요.');
      return;
    }
    
    // URL 업데이트
    navigate(`/search?q=${encodeURIComponent(searchQuery)}&type=${searchType}`);
    
    // 검색 실행
    executeSearch(searchQuery, searchType);
  }, [searchQuery, searchType, navigate, executeSearch]);

  // 페이지 변경 처리
  const handlePageChange = useCallback((page: number) => {
    executeSearch(searchQuery, searchType, page);
  }, [searchQuery, searchType, executeSearch]);

  // 검색 유형 변경 핸들러
  const handleSearchTypeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setSearchType(e.target.value);
  }, []);

  // 검색어 변경 핸들러
  const handleSearchInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    if (error && e.target.value.length >= 3) {
      setError(null);
    }
  }, [error]);

  return (
    <SearchContainer>
      <SearchContent>
        <SearchBox>
          <SearchHeader>
            <SearchTitle>전체 게시물 검색</SearchTitle>
          </SearchHeader>
          <SearchForm onSubmit={handleSearch}>
            <SearchSelect 
              value={searchType}
              onChange={handleSearchTypeChange}
            >
              <option value="1">제목</option>
              <option value="2">내용</option>
              <option value="3">작성자</option>
            </SearchSelect>
            <SearchInput 
              type="text"
              value={searchQuery}
              onChange={handleSearchInputChange}
              placeholder="검색어를 입력하세요 (최소 3자 이상)"
              autoFocus
            />
            <SearchButton type="submit">검색</SearchButton>
          </SearchForm>
          {error && <ErrorMessage>{error}</ErrorMessage>}
        </SearchBox>

        <ResultsContainer>
          {loading ? (
            <LoadingMessage>검색 중입니다...</LoadingMessage>
          ) : (
            <>
              {initialSearchQuery && !error && (
                <ResultInfo>
                  <strong>"{initialSearchQuery}"</strong>에 대한 검색 결과: 총 <strong>{pagination.totalItems}</strong>개의 게시물
                </ResultInfo>
              )}
              
              {!error && (
                <PostList 
                  list={posts}
                  pagination={pagination}
                  customPageHandler={handlePageChange}
                  title="검색 결과"
                  showHeader={true}
                  showSearch={false}
                  showWriteButton={false}
                />
              )}
            </>
          )}
        </ResultsContainer>
      </SearchContent>
    </SearchContainer>
  );
};

export default Search; 